import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CreditCard,
  Plus,
  Star,
  Trash2,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Calendar,
  Hash,
  Loader2,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { healthPlansApi } from '@/api/endpoints/healthplans.api';
import type { PatientHealthPlanDto, CreatePatientHealthPlanRequest, UpdatePatientHealthPlanRequest, DependencyType } from '@/types/healthplan.types';

interface HealthPlansCardProps {
  patientId: string;
}

const initialFormState: Partial<CreatePatientHealthPlanRequest> = {
  healthPlanId: '',
  cardNumber: '',
  holderName: '',
  holderCpf: '',
  dependencyType: undefined,
  validFrom: '',
  validUntil: '',
  isPrimary: false,
};

export function HealthPlansCard({ patientId }: HealthPlansCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(true);
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [deletePlan, setDeletePlan] = useState<PatientHealthPlanDto | null>(null);
  const [editPlan, setEditPlan] = useState<PatientHealthPlanDto | null>(null);
  const [addPlanForm, setAddPlanForm] = useState(initialFormState);
  const [editPlanForm, setEditPlanForm] = useState<Partial<UpdatePatientHealthPlanRequest>>({});

  const { data: healthPlans, isLoading } = useQuery({
    queryKey: ['patient-health-plans', patientId],
    queryFn: () => healthPlansApi.getPatientHealthPlans(patientId),
  });

  const { data: availablePlans } = useQuery({
    queryKey: ['health-plans-available'],
    queryFn: () => healthPlansApi.getAll(true),
    enabled: showAddPlan,
  });

  const addPlanMutation = useMutation({
    mutationFn: healthPlansApi.addHealthPlanToPatient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-health-plans', patientId] });
      toast({ title: 'Carteirinha adicionada com sucesso' });
      setShowAddPlan(false);
      setAddPlanForm(initialFormState);
    },
    onError: () => {
      toast({ title: 'Erro ao adicionar carteirinha', variant: 'destructive' });
    },
  });

  const handleAddPlan = () => {
    if (!addPlanForm.healthPlanId || !addPlanForm.cardNumber) {
      toast({ title: 'Preencha os campos obrigatórios', variant: 'destructive' });
      return;
    }

    addPlanMutation.mutate({
      patientId,
      healthPlanId: addPlanForm.healthPlanId,
      cardNumber: addPlanForm.cardNumber,
      holderName: addPlanForm.holderName || undefined,
      holderCpf: addPlanForm.holderCpf || undefined,
      dependencyType: addPlanForm.dependencyType || undefined,
      validFrom: addPlanForm.validFrom || undefined,
      validUntil: addPlanForm.validUntil || undefined,
      isPrimary: addPlanForm.isPrimary,
    });
  };

  const setPrimaryMutation = useMutation({
    mutationFn: healthPlansApi.setPrimaryHealthPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-health-plans', patientId] });
      toast({ title: 'Carteirinha principal definida' });
    },
    onError: () => {
      toast({ title: 'Erro ao definir carteirinha principal', variant: 'destructive' });
    },
  });

  const removeMutation = useMutation({
    mutationFn: healthPlansApi.removeHealthPlanFromPatient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-health-plans', patientId] });
      toast({ title: 'Carteirinha removida' });
      setDeletePlan(null);
    },
    onError: () => {
      toast({ title: 'Erro ao remover carteirinha', variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePatientHealthPlanRequest }) =>
      healthPlansApi.updatePatientHealthPlan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-health-plans', patientId] });
      toast({ title: 'Carteirinha atualizada com sucesso' });
      setEditPlan(null);
      setEditPlanForm({});
    },
    onError: () => {
      toast({ title: 'Erro ao atualizar carteirinha', variant: 'destructive' });
    },
  });

  const handleOpenEditDialog = (plan: PatientHealthPlanDto) => {
    setEditPlan(plan);
    setEditPlanForm({
      cardNumber: plan.cardNumber,
      holderName: plan.holderName || '',
      holderCpf: plan.holderCpf || '',
      dependencyType: plan.dependencyType,
      validFrom: plan.validFrom || '',
      validUntil: plan.validUntil || '',
    });
  };

  const handleUpdatePlan = () => {
    if (!editPlan || !editPlanForm.cardNumber) {
      toast({ title: 'Preencha o número da carteirinha', variant: 'destructive' });
      return;
    }

    updateMutation.mutate({
      id: editPlan.id,
      data: {
        cardNumber: editPlanForm.cardNumber,
        holderName: editPlanForm.holderName || undefined,
        holderCpf: editPlanForm.holderCpf || undefined,
        dependencyType: editPlanForm.dependencyType || undefined,
        validFrom: editPlanForm.validFrom || undefined,
        validUntil: editPlanForm.validUntil || undefined,
      },
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-green-100 text-green-800">Ativa</Badge>;
      case 'SUSPENDED':
        return <Badge className="bg-yellow-100 text-yellow-800">Suspensa</Badge>;
      case 'CANCELLED':
        return <Badge className="bg-red-100 text-red-800">Cancelada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Planos de Saúde
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Planos de Saúde
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
        <CardDescription>
          {healthPlans?.length || 0} carteirinha(s) cadastrada(s)
        </CardDescription>
      </CardHeader>
      {isOpen && (
        <CardContent>
          {!healthPlans || healthPlans.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted-foreground text-sm mb-3">
                Nenhuma carteirinha cadastrada
              </p>
              <Button size="sm" variant="outline" onClick={() => setShowAddPlan(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Carteirinha
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {healthPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`p-3 rounded-lg border ${
                    plan.isPrimary ? 'border-primary bg-primary/5' : 'bg-muted/50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{plan.healthPlanName}</span>
                      {plan.isPrimary && (
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      )}
                    </div>
                    {getStatusBadge(plan.status)}
                  </div>

                  <div className="space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Hash className="h-3 w-3" />
                      <span>Carteirinha: {plan.cardNumber}</span>
                    </div>
                    {plan.validUntil && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3" />
                        <span>
                          Validade:{' '}
                          {format(new Date(plan.validUntil), 'dd/MM/yyyy', { locale: ptBR })}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mt-3">
                    {!plan.isPrimary && plan.status === 'ACTIVE' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPrimaryMutation.mutate(plan.id)}
                      >
                        <Star className="mr-1 h-3 w-3" />
                        Definir Principal
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenEditDialog(plan)}
                    >
                      <Pencil className="mr-1 h-3 w-3" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive"
                      onClick={() => setDeletePlan(plan)}
                    >
                      <Trash2 className="mr-1 h-3 w-3" />
                      Remover
                    </Button>
                  </div>
                </div>
              ))}

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setShowAddPlan(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Carteirinha
              </Button>
            </div>
          )}
        </CardContent>
      )}

      {/* Add Plan Dialog */}
      <Dialog open={showAddPlan} onOpenChange={setShowAddPlan}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Carteirinha</DialogTitle>
            <DialogDescription>
              Adicione uma nova carteirinha de plano de saúde
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="health-plan">Plano de Saúde *</Label>
              <Select
                value={addPlanForm.healthPlanId || ''}
                onValueChange={(value) =>
                  setAddPlanForm((prev) => ({ ...prev, healthPlanId: value }))
                }
              >
                <SelectTrigger id="health-plan">
                  <SelectValue placeholder="Selecione um plano" />
                </SelectTrigger>
                <SelectContent>
                  {availablePlans?.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name} ({plan.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="card-number">Número da Carteirinha *</Label>
              <Input
                id="card-number"
                placeholder="Ex: 123456789"
                value={addPlanForm.cardNumber || ''}
                onChange={(e) =>
                  setAddPlanForm((prev) => ({ ...prev, cardNumber: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dependency-type">Tipo de Dependência</Label>
              <Select
                value={addPlanForm.dependencyType || ''}
                onValueChange={(value) =>
                  setAddPlanForm((prev) => ({
                    ...prev,
                    dependencyType: value as DependencyType,
                  }))
                }
              >
                <SelectTrigger id="dependency-type">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HOLDER">Titular</SelectItem>
                  <SelectItem value="SPOUSE">Cônjuge</SelectItem>
                  <SelectItem value="CHILD">Filho(a)</SelectItem>
                  <SelectItem value="PARENT">Pai/Mãe</SelectItem>
                  <SelectItem value="OTHER">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="holder-name">Nome do Titular</Label>
                <Input
                  id="holder-name"
                  placeholder="Nome do titular"
                  value={addPlanForm.holderName || ''}
                  onChange={(e) =>
                    setAddPlanForm((prev) => ({ ...prev, holderName: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="holder-cpf">CPF do Titular</Label>
                <Input
                  id="holder-cpf"
                  placeholder="000.000.000-00"
                  value={addPlanForm.holderCpf || ''}
                  onChange={(e) =>
                    setAddPlanForm((prev) => ({ ...prev, holderCpf: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valid-from">Válido a partir de</Label>
                <Input
                  id="valid-from"
                  type="date"
                  value={addPlanForm.validFrom || ''}
                  onChange={(e) =>
                    setAddPlanForm((prev) => ({ ...prev, validFrom: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valid-until">Válido até</Label>
                <Input
                  id="valid-until"
                  type="date"
                  value={addPlanForm.validUntil || ''}
                  onChange={(e) =>
                    setAddPlanForm((prev) => ({ ...prev, validUntil: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is-primary"
                checked={addPlanForm.isPrimary}
                onCheckedChange={(checked) =>
                  setAddPlanForm((prev) => ({ ...prev, isPrimary: checked === true }))
                }
              />
              <Label htmlFor="is-primary" className="text-sm font-normal">
                Definir como carteirinha principal
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPlan(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAddPlan}
              disabled={addPlanMutation.isPending || !addPlanForm.healthPlanId || !addPlanForm.cardNumber}
            >
              {addPlanMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletePlan} onOpenChange={() => setDeletePlan(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Carteirinha</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover a carteirinha "{deletePlan?.cardNumber}" do plano{' '}
              {deletePlan?.healthPlanName}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletePlan && removeMutation.mutate(deletePlan.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Plan Dialog */}
      <Dialog open={!!editPlan} onOpenChange={() => setEditPlan(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Carteirinha</DialogTitle>
            <DialogDescription>
              Edite os dados da carteirinha do plano {editPlan?.healthPlanName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-card-number">Número da Carteirinha *</Label>
              <Input
                id="edit-card-number"
                placeholder="Ex: 123456789"
                value={editPlanForm.cardNumber || ''}
                onChange={(e) =>
                  setEditPlanForm((prev) => ({ ...prev, cardNumber: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-dependency-type">Tipo de Dependência</Label>
              <Select
                value={editPlanForm.dependencyType || '_none'}
                onValueChange={(value) =>
                  setEditPlanForm((prev) => ({
                    ...prev,
                    dependencyType: value === '_none' ? undefined : (value as DependencyType),
                  }))
                }
              >
                <SelectTrigger id="edit-dependency-type">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Não informado</SelectItem>
                  <SelectItem value="HOLDER">Titular</SelectItem>
                  <SelectItem value="SPOUSE">Cônjuge</SelectItem>
                  <SelectItem value="CHILD">Filho(a)</SelectItem>
                  <SelectItem value="PARENT">Pai/Mãe</SelectItem>
                  <SelectItem value="OTHER">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-holder-name">Nome do Titular</Label>
                <Input
                  id="edit-holder-name"
                  placeholder="Nome do titular"
                  value={editPlanForm.holderName || ''}
                  onChange={(e) =>
                    setEditPlanForm((prev) => ({ ...prev, holderName: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-holder-cpf">CPF do Titular</Label>
                <Input
                  id="edit-holder-cpf"
                  placeholder="000.000.000-00"
                  value={editPlanForm.holderCpf || ''}
                  onChange={(e) =>
                    setEditPlanForm((prev) => ({ ...prev, holderCpf: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-valid-from">Válido a partir de</Label>
                <Input
                  id="edit-valid-from"
                  type="date"
                  value={editPlanForm.validFrom || ''}
                  onChange={(e) =>
                    setEditPlanForm((prev) => ({ ...prev, validFrom: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-valid-until">Válido até</Label>
                <Input
                  id="edit-valid-until"
                  type="date"
                  value={editPlanForm.validUntil || ''}
                  onChange={(e) =>
                    setEditPlanForm((prev) => ({ ...prev, validUntil: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPlan(null)}>
              Cancelar
            </Button>
            <Button
              onClick={handleUpdatePlan}
              disabled={updateMutation.isPending || !editPlanForm.cardNumber}
            >
              {updateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
